import { useRef, useState } from 'react';
import { Webhook } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { SecretRevealDialog, type RevealedSecret } from '@/components/SecretRevealDialog';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatAgo } from '@/lib/ago';
import { api } from '@/lib/api';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { cn } from '@/lib/utils';
import {
  eventTypesLabel,
  receiverScopeLabel,
  webhookErrorMessage,
  SIGNING_SECRET_ACK,
  SIGNING_SECRET_WARNING,
} from '@/lib/webhooks';
import type { WebhookReceiverSummary, WebhookReceiverWithSecret } from '@/types/webhook';
import { WebhookDeliveriesDialog } from './WebhookDeliveriesDialog';
import { WebhookReceiverDialog, type ReceiverDialogTarget } from './WebhookReceiverDialog';

function createdReveal(r: WebhookReceiverWithSecret): RevealedSecret {
  return {
    id: `create:${r.id}`,
    title: 'Receiver created',
    description: `Copy the signing secret for '${r.name}' now. It will not be shown again.`,
    label: 'Signing secret',
    value: r.secret,
    warning: SIGNING_SECRET_WARNING,
    acknowledgement: SIGNING_SECRET_ACK,
  };
}

export function WebhookReceiverList() {
  const receivers = usePolledData(() => api.webhooks.list({ limit: 200 }), 30_000);
  const keys = usePolledData(() => api.apiKeys.list());
  const [formTarget, setFormTarget] = useState<ReceiverDialogTarget>(null);
  const [reveal, setReveal] = useState<RevealedSecret | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<WebhookReceiverSummary | null>(null);
  // `deleting` keeps the target through the dialog's exit animation; `deleteOpen` controls visibility.
  const [deleting, setDeleting] = useState<WebhookReceiverSummary | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteBusyRef = useRef(false);

  const runDelete = async () => {
    const target = deleting;
    if (!target || deleteBusyRef.current) return;
    deleteBusyRef.current = true;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.webhooks.delete(target.id);
      setDeleteOpen(false);
      toast.success('Receiver deleted');
      receivers.refetch();
    } catch (err) {
      setDeleteError(webhookErrorMessage(err, 'Failed to delete receiver'));
    } finally {
      deleteBusyRef.current = false;
      setDeleteBusy(false);
    }
  };

  const keyNames = new Map((keys.data ?? []).map((k) => [k.id, k.name]));
  const page = receivers.data;
  const rows = page?.items ?? [];
  // Poll time, not Date.now(): keeps render pure.
  const now = receivers.updatedAt ?? undefined;
  const openCreate = () => setFormTarget({ mode: 'create' });

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <CardTitle>Webhook receivers</CardTitle>
              <CardDescription>HTTPS endpoints that receive signed app events: deploys, status changes, migrations and deletions.</CardDescription>
            </div>
            <Button onClick={openCreate}>Add receiver</Button>
          </div>
        </CardHeader>
        <CardContent data-focus-fallback tabIndex={-1} className="focus:outline-none">
          {receivers.loading ? (
            <p role="status" className="py-8 text-center text-muted-foreground">
              Loading webhook receivers…
            </p>
          ) : page === undefined ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              <span>{receivers.error ?? 'Failed to load webhook receivers'}</span>
              <Button size="sm" variant="outline" onClick={receivers.refetch}>
                Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              as="h3"
              icon={Webhook}
              title="No webhook receivers"
              description="Webhooks push app events to your service as they happen, so it does not have to poll. Use Add receiver to register an HTTPS endpoint."
            />
          ) : (
            <>
              {receivers.error ? (
                <p role="alert" className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  Could not refresh webhook receivers: {receivers.error}
                </p>
              ) : null}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Last delivery</TableHead>
                    <TableHead>Failures 24h</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const lastAt = r.last_delivery_at ? Date.parse(r.last_delivery_at) : NaN;
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="max-w-[16rem]">
                          <div className="font-medium [overflow-wrap:anywhere]">{r.name}</div>
                          <div className="truncate font-mono text-xs text-muted-foreground" title={r.url}>
                            {r.url}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{receiverScopeLabel(r.api_key_id, keyNames)}</TableCell>
                        <TableCell className="text-sm" title={r.event_types.length > 2 ? r.event_types.join(', ') : undefined}>
                          {eventTypesLabel(r.event_types)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {r.last_delivery_status ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <StatusBadge status={r.last_delivery_status} />
                              {Number.isNaN(lastAt) ? null : (
                                <time dateTime={r.last_delivery_at ?? undefined} title={new Date(lastAt).toLocaleString()} className="text-muted-foreground">
                                  {formatAgo(lastAt, now)}
                                </time>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className={cn('text-sm tabular-nums', r.failed_24h > 0 && 'font-medium text-status-danger')}>{r.failed_24h}</TableCell>
                        <TableCell>
                          <StatusBadge status={r.is_active ? 'active' : 'inactive'} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button size="sm" variant="outline" aria-label={`Deliveries for ${r.name}`} onClick={() => setDeliveriesFor(r)}>
                              Deliveries
                            </Button>
                            <Button size="sm" variant="outline" aria-label={`Edit ${r.name}`} onClick={() => setFormTarget({ mode: 'edit', receiver: r })}>
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              aria-label={`Delete ${r.name}`}
                              onClick={() => {
                                setDeleteError(null);
                                setDeleting(r);
                                setDeleteOpen(true);
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {page.total > rows.length ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Showing {rows.length} of {page.total} receivers.
                </p>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <WebhookReceiverDialog
        target={formTarget}
        apiKeys={keys.data}
        apiKeysError={keys.error}
        onClose={() => setFormTarget(null)}
        onCreated={(created) => {
          setFormTarget(null);
          setReveal(createdReveal(created));
          receivers.refetch();
        }}
        onSaved={() => {
          setFormTarget(null);
          receivers.refetch();
        }}
      />

      <WebhookDeliveriesDialog
        receiver={deliveriesFor}
        onClose={() => {
          setDeliveriesFor(null);
          // Tests and redeliveries change the row's last delivery and failure count.
          receivers.refetch();
        }}
      />

      <SecretRevealDialog reveal={reveal} onDone={() => setReveal(null)} />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => {
          if (!next && !deleteBusyRef.current) {
            setDeleteOpen(false);
            setDeleteError(null);
          }
        }}
        title="Delete receiver?"
        description={deleting ? `Delete receiver '${deleting.name}'? Pending deliveries to it will stop.` : undefined}
        confirmLabel={deleteBusy ? 'Deleting…' : 'Delete'}
        destructive
        loading={deleteBusy}
        error={deleteError}
        onConfirm={() => void runDelete()}
      />
    </>
  );
}
